import { forwardRef, useEffect, useRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import './Dialog.css';
import { ICON_SIZE } from '../../icons';
import { isImeComposing } from '../../../utils/imeUtils';

/**
 * shadcn 模式的对话框：Radix 出行为（portal、focus trap、滚动锁、Escape、
 * 点 backdrop 关闭、焦点归还、多层堆叠），rds token 出面。
 * 公开 API：isOpen / onClose / title / description / children /
 * footer / size / animated / showClose / closeOnBackdrop / closeOnEscape /
 * closeLabel / ariaLabel / initialFocusRef / className / overlayClassName /
 * centered / onAfterClose（焦点归还后执行）。
 */
const Dialog = forwardRef(({
  isOpen,
  onClose,
  onAfterClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  animated = true,
  showClose = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
  closeLabel = 'Close',
  ariaLabel,
  initialFocusRef,
  className = '',
  overlayClassName = '',
  centered = false,
}, forwardedRef) => {
  // 只有 ariaLabel 的对话框也要有 Title 元素（视觉隐藏），否则 Radix 会
  // 警告且屏幕阅读器拿不到可读名称。
  const headingText = title ?? (ariaLabel ? ariaLabel : undefined);

  // Radix 在 jsdom 下不保证焦点归还（真实浏览器靠 FocusScope）。保留旧手写版
  // 经过验证的归还：关闭后的下一帧把焦点送回打开前的元素（幂等，Radix 先归
  // 也不冲突）。注意：本 effect 必须在下方 early return 之前声明。
  const returnFocusRef = useRef(null);
  const afterCloseRef = useRef(onAfterClose);
  useEffect(() => { afterCloseRef.current = onAfterClose; }, [onAfterClose]);
  useEffect(() => {
    if (isOpen) {
      returnFocusRef.current = document.activeElement;
      return undefined;
    }
    const target = returnFocusRef.current;
    returnFocusRef.current = null;
    if (!target) return undefined;
    const frame = requestAnimationFrame(() => {
      if (document.contains(target)) target.focus?.({ preventScroll: true });
      afterCloseRef.current?.();
    });
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  if (!isOpen) return null;

  if (!title && !ariaLabel) {
    throw new Error('Dialog requires either a title or ariaLabel.');
  }

  const sizeClass = ['sm', 'md', 'lg', 'xl'].includes(size) ? size : 'md';

  return (
    <DialogPrimitive.Root
      open
      onOpenChange={(open) => { if (!open) onClose?.(); }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={`rds-dialog__backdrop ${animated ? 'rds-dialog__backdrop--animated' : ''} ${overlayClassName}`.trim()}
        />
        <DialogPrimitive.Content
          ref={forwardedRef}
          className={`rds-dialog rds-dialog--${sizeClass} ${animated ? 'rds-dialog--animated' : ''} ${className}`.trim()}
          role="dialog"
          aria-modal="true"
          {...(!description ? { 'aria-describedby': undefined } : {})}
          tabIndex={-1}
          onClick={(event) => event.stopPropagation()}
          onEscapeKeyDown={(event) => {
            // Radix listens on Escape in the capture phase, so this is the
            // single choke point every rds dialog shares: don't dismiss while
            // an IME composition is active (Esc cancels candidates, not the
            // dialog), nor when the dialog opts out of Escape entirely.
            if (isImeComposing(event) || !closeOnEscape) event.preventDefault();
          }}
          onInteractOutside={(event) => { if (!closeOnBackdrop) event.preventDefault(); }}
          onCloseAutoFocus={(event) => {
            // Restoring the trigger must not cancel an explicit reader jump.
            event.preventDefault();
            const target = returnFocusRef.current;
            if (target && document.contains(target)) target.focus?.({ preventScroll: true });
          }}
          onOpenAutoFocus={(event) => {
            if (initialFocusRef?.current) {
              event.preventDefault();
              initialFocusRef.current.focus();
            }
          }}
        >
          {showClose && (
            <DialogPrimitive.Close asChild>
              <button
                type="button"
                className="rds-dialog__close rds-focusable"
                aria-label={closeLabel}
              >
                <X size={ICON_SIZE.md} aria-hidden="true" />
              </button>
            </DialogPrimitive.Close>
          )}

          {headingText && (
            <DialogPrimitive.Title asChild>
              <h2
                className={`rds-dialog__title ${title ? '' : 'rds-visually-hidden'}`.trim()}
              >
                {headingText}
              </h2>
            </DialogPrimitive.Title>
          )}
          {description && <DialogPrimitive.Description asChild><p className="rds-dialog__description">{description}</p></DialogPrimitive.Description>}

          {children}

          {footer && (
            <div className={`rds-dialog__footer ${centered ? 'rds-dialog__footer--center' : ''}`.trim()}>
              {footer}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
});

Dialog.displayName = 'Dialog';

export default Dialog;
